/**
 * Shared paginated footer with clickable page numbers + ellipses + first /
 * prev / next / last. Mirrors the DataTable component's pagination UX so the
 * billing pages (WeNext / PhotonX / Maya — Clients, Proformas, Invoices) feel
 * identical to /admin/clients.
 *
 * Page numbers are 1-indexed externally for consistency with DataTable.
 */
import React from 'react';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  MoreHorizontal,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

interface PaginationFooterProps {
  /** Total row count from the backend. */
  totalItems: number;
  /** Current page, 1-indexed. */
  currentPage: number;
  /** Rows per page. */
  pageSize: number;
  /** Triggered when the user navigates to a different page. 1-indexed. */
  onPageChange: (page: number) => void;
  /** Triggered when the user picks a new page size. */
  onPageSizeChange: (size: number) => void;
  /** Override the page-size dropdown options. */
  pageSizeOptions?: readonly number[];
  /** Singular noun for the "X of Y results" copy. Defaults to "results". */
  itemNoun?: string;
}

export function PaginationFooter({
  totalItems,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  itemNoun = 'results',
}: PaginationFooterProps) {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const showingFrom = totalItems > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const showingTo = Math.min(currentPage * pageSize, totalItems);

  // Window of page buttons around the current page, with ellipsis collapsing
  // unrelated gaps. Same algorithm as web/src/components/reusable/DataTable.tsx.
  const pages: (number | 'ellipsis')[] = [];
  const startPage = Math.max(1, currentPage - 1);
  const endPage = Math.min(totalPages, currentPage + 1);
  const showStartEllipsis = startPage > 1;
  const showEndEllipsis = endPage < totalPages;
  const pagesBefore = startPage - 1;
  const pagesAfter = totalPages - endPage;
  const showOnlyStartEllipsis = showStartEllipsis && showEndEllipsis && pagesBefore >= pagesAfter;
  const showOnlyEndEllipsis = showStartEllipsis && showEndEllipsis && pagesAfter > pagesBefore;

  if (showStartEllipsis && !showOnlyEndEllipsis) {
    pages.push(1);
    pages.push('ellipsis');
  }
  for (let i = startPage; i <= endPage; i++) pages.push(i);
  if (showEndEllipsis && !showOnlyStartEllipsis) {
    pages.push('ellipsis');
    pages.push(totalPages);
  }

  return (
    <div className="mt-3 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center sm:gap-4">
      <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:gap-4">
        <div className="text-xs text-muted-foreground sm:text-sm">
          Showing {showingFrom} to {showingTo} of {totalItems} {itemNoun}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground sm:text-sm">Rows per page:</span>
          <Select
            value={String(pageSize)}
            onValueChange={(v) => onPageSizeChange(Number(v))}
          >
            <SelectTrigger className="h-7 w-[70px] text-xs sm:h-8 sm:text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pageSizeOptions.map((n) => (
                <SelectItem key={n} value={String(n)}>
                  {n}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="flex w-full min-w-0 flex-wrap items-center justify-end gap-1 sm:w-auto sm:gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(1)}
            disabled={currentPage === 1}
            className="h-7 w-7 flex-shrink-0 p-0 sm:h-8 sm:w-8"
          >
            <ChevronsLeft className="h-3 w-3 sm:h-4 sm:w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="h-7 w-7 flex-shrink-0 p-0 sm:h-8 sm:w-8"
          >
            <ChevronLeft className="h-3 w-3 sm:h-4 sm:w-4" />
          </Button>
          <div className="flex min-w-0 flex-wrap items-center gap-0.5 sm:gap-1">
            {pages.map((p, idx) =>
              p === 'ellipsis' ? (
                <div
                  key={`ellipsis-${idx}`}
                  className="flex h-7 w-7 flex-shrink-0 items-center justify-center sm:h-8 sm:w-8"
                >
                  <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                </div>
              ) : (
                <Button
                  key={p}
                  variant={currentPage === p ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => onPageChange(p)}
                  className={cn(
                    'h-7 min-w-[2.25rem] flex-shrink-0 px-2 text-xs sm:h-8 sm:min-w-[2.5rem] sm:text-sm',
                    currentPage === p && 'bg-black text-white hover:bg-black/90',
                  )}
                >
                  {p}
                </Button>
              ),
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="h-7 w-7 flex-shrink-0 p-0 sm:h-8 sm:w-8"
          >
            <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(totalPages)}
            disabled={currentPage === totalPages}
            className="h-7 w-7 flex-shrink-0 p-0 sm:h-8 sm:w-8"
          >
            <ChevronsRight className="h-3 w-3 sm:h-4 sm:w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
